'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { LoginSchema, LoginData } from '@/lib/schemas'
import { useAuthStore } from '@/lib/stores/auth-store'
import { useThemeStore } from '@/lib/stores/theme-store'
import { Form } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { ButtonLoading } from '@/components/ui/button-loading'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Checkbox } from '@/components/ui/checkbox'
import { Separator } from '@/components/ui/separator'
import { TextField } from '@/components/forms/form-field'
import { useRouter, usePathname } from 'next/navigation'
import { isSafeInternalRedirectPath } from '@/lib/utils/safe-redirect'
import Link from 'next/link'
import { useState, useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { trackEvent } from '@/lib/analytics'

interface LoginFormProps {
  onSuccess?: () => void
  className?: string
  /** Valor de ?redirect= na URL de login (path interno + query) */
  redirectAfterLogin?: string | null
  /** E-mail to prefill (links sent after a purchase use `/?email=`). */
  initialEmail?: string | null
}

export function LoginForm({ onSuccess, className, redirectAfterLogin, initialEmail }: LoginFormProps) {
  const { t } = useTranslation(['auth', 'common'])
  const router = useRouter()
  const pathname = usePathname()
  const { login, isLoading, isNavigating, setNavigating } = useAuthStore()
  const { currentTheme } = useThemeStore()
  const [rememberMe, setRememberMe] = useState(false)
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const form = useForm<LoginData>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      email: initialEmail && initialEmail.length <= 200 && initialEmail.includes('@') ? initialEmail : '',
      password: ''
    }
  })

  const handleSubmit = async (data: LoginData) => {
    try {
      await login(data.email, data.password, currentTheme?.mentor_id)

      trackEvent('login_completed', { method: 'password' })

      // Store remember me preference
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true')
      } else {
        localStorage.removeItem('rememberMe')
      }

      // Set navigating state immediately after successful login
      setNavigating(true)

      // Timeout de segurança - garante que loading nunca trava permanentemente
      navigationTimeoutRef.current = setTimeout(() => {
        setNavigating(false)
      }, 1500) // 1.5 segundos é suficiente para navegação

      toast.success(t('auth:login.success'))

      if (onSuccess) {
        onSuccess()
      } else if (isSafeInternalRedirectPath(redirectAfterLogin)) {
        router.push(redirectAfterLogin)
      } else {
        router.push('/bem-vindo')
      }
    } catch (error) {
      // Limpar timeout se houver
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current)
        navigationTimeoutRef.current = null
      }
      setNavigating(false)

      // Tratar erros de segurança específicos
      if (error instanceof Error) {
        switch (error.message) {
          case 'REQUIRE_PASSWORD_CHANGE':
            toast.info(t('auth:security.requirePasswordChange'))
            router.push('/trocar-senha')
            return

          case 'NEW_DEVICE_VERIFICATION':
            toast.info(t('auth:security.newDeviceVerification'))
            return

          case 'DEVICE_LIMIT_REACHED':
            toast.error(t('auth:security.deviceLimitReached'))
            return

          case 'MEMBER_AREA_BLOCKED':
            toast.error(
              currentTheme?.mentor_email
                ? `Área de Membros desativada. Busque mais informações pelo email ${currentTheme.mentor_email}.`
                : 'Área de Membros desativada. Busque mais informações com o mentor responsável.'
            )
            return

          case 'INVALID_CREDENTIALS':
            toast.error(t('auth:errors.invalidCredentials'))
            return

          case 'BACKEND_UNAVAILABLE':
            toast.error(t('auth:errors.backendUnavailable'))
            return

          default:
            toast.error(error.message || t('auth:login.error'))
        }
      } else {
        toast.error(t('auth:login.error'))
      }
    }
  }


  // Pathname monitoring - detecta mudança de rota precisa
  useEffect(() => {
    if (isNavigating && pathname !== '/' && pathname !== '') {
      // Navegação detectada para qualquer rota diferente da login
      // Limpar timeout e parar loading
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current)
        navigationTimeoutRef.current = null
      }
      setNavigating(false)
    }
  }, [pathname, isNavigating, setNavigating])

  // Cleanup navigating state on unmount
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current)
        navigationTimeoutRef.current = null
      }
      setNavigating(false)
    }
  }, [setNavigating])


  return (
    <div className={className}>
      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(handleSubmit)}>
          <TextField
            control={form.control}
            name="email"
            label={t('auth:login.email.label')}
            type="email"
            placeholder={t('auth:login.email.placeholder')}
            disabled={isLoading || isNavigating}
            inputClassName="transition-colors duration-200"
            inputStyle={{
              color: 'var(--login-form-input-text)',
              backgroundColor: 'var(--login-form-input-bg)',
            }}
          />

          <TextField
            control={form.control}
            name="password"
            label={t('auth:login.password.label')}
            type="password"
            placeholder={t('auth:login.password.placeholder')}
            disabled={isLoading || isNavigating}
            inputClassName="transition-colors duration-200"
            inputStyle={{
              color: 'var(--login-form-input-text)',
              backgroundColor: 'var(--login-form-input-bg)',
            }}
          />

          {(form.formState.errors.email || form.formState.errors.password) && (
            <Alert variant="destructive">
              <AlertDescription>
                {form.formState.errors.email?.message || form.formState.errors.password?.message}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="remember-me"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                disabled={isLoading || isNavigating}
              />
              <label
                htmlFor="remember-me"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {t('auth:login.rememberMe')}
              </label>
            </div>

            <Link
              href="/recuperar-senha"
              className="flex items-center gap-1 text-xs hover:underline transition-colors duration-200"
              style={{ color: 'var(--login-form-forgot)' }}
            >
              {t('auth:login.forgotPassword')}
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <rect x="3" y="11" width="18" height="10" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </Link>
          </div>

          <Button
            type="submit"
            disabled={isLoading || isNavigating}
            className="w-full transition-all duration-200 hover:opacity-90"
            style={{
              backgroundColor: 'var(--login-form-button-bg)',
              color: 'var(--login-form-button-text)',
            }}
          >
            <ButtonLoading
              isLoading={isLoading || isNavigating}
              loadingText={isNavigating ? t('auth:login.redirecting') : t('auth:login.submitting')}
            >
              {t('auth:login.submit')}
            </ButtonLoading>
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            {/* <div className="relative flex justify-center text-xs uppercase">
              <span className="px-2 text-muted-foreground bg-background">{t('auth:login.orContinueWith')}</span>
            </div> */}
          </div>

        </form>
      </Form>
    </div>
  )
}
